/**
 * Runtime URL derivation for the builder SPA (build-plan §3.2).
 *
 * Why runtime, not build-time: the SAME builder bundle ships to local / preprod / prod (one Docker
 * image, three deploys — ADR-0005). Build-time env vars would force three separate builds. The
 * hostname maps the env unambiguously, exactly mirroring the dashboard's `api-base.ts`
 * (`deriveApiBaseUrl`), so the two apps resolve the API the same way and the SameSite=Strict refresh
 * cookie is sent on both.
 *
 * Resolution rule (kept dumb on purpose — easier to audit than env-detection libraries):
 *  - `builder.shopiwize.com` (PROD root) → `https://api.shopiwize.com`
 *  - `builder.<x>.aquilixshop.com` (preprod) → `https://api.<x>.aquilixshop.com`
 *  - `*.aquilixshop.localhost` (Caddy dev) → `http://api.aquilixshop.localhost`
 *  - plain `localhost` / `127.0.0.1` dev → `http://localhost:8080` (the back, talked to directly)
 *
 * The `builder.* ↔ api.*` pair MUST stay same-site (both under the SAME platform domain) so the
 * `SameSite=Strict` refresh cookie reaches the API on the silent-refresh POST (build-plan §3.4). In
 * dev, `localhost:3000 ↔ localhost:8080` is one site (port is not part of "site"), so the cookie is
 * sent and a reload survives.
 */
/**
 * The platform's public domains, in priority order. **PROD is `shopiwize.com`**; `aquilixshop.com`
 * is preprod. Both are listed because ONE bundle serves both (the one-image-three-deploys rule
 * above), so the suffix cannot be baked in at build time.
 *
 * This used to be a single hardcoded `.aquilixshop.com` test that fell through to
 * `http://localhost:8080` for every other host. Served from `admin.shopiwize.com`, the dashboard
 * therefore called the laptop's port 8080 and NOTHING worked — no login, no API, a blank app. Adding
 * a domain to the platform must never again be a silent, total outage: keep this list in step with
 * the edge (Caddy hosts) and with the backend's `STOREFRONT_DOMAIN_SUFFIXES`.
 */
const PLATFORM_HOST_SUFFIXES = ['.shopiwize.com', '.aquilixshop.com'] as const;

/** The platform domain this hostname belongs to, or `null` when it is not a platform host. */
function platformHostSuffix(hostname: string): string | null {
  return PLATFORM_HOST_SUFFIXES.find((suffix) => hostname.endsWith(suffix)) ?? null;
}

export function deriveApiBaseUrl(): string {
  if (typeof window === 'undefined') return 'http://localhost:8080';
  const { hostname } = window.location;
  if (platformHostSuffix(hostname)) {
    return `https://${hostname.replace(/^builder\./, 'api.')}`;
  }
  if (hostname.endsWith('.aquilixshop.localhost')) {
    return 'http://api.aquilixshop.localhost';
  }
  return 'http://localhost:8080';
}

/**
 * The dashboard origin — where login lives and where the builder bounces on a failed silent refresh
 * (the builder has NO login UI of its own, build-plan §3.3). Mirrors the api derivation:
 *  - `builder.<x>.aquilixshop.com` → `https://admin.<x>.aquilixshop.com`
 *  - `*.aquilixshop.localhost` → `http://admin.aquilixshop.localhost`
 *  - plain localhost dev → `http://localhost:4200` (the dashboard dev port)
 */
export function deriveDashboardUrl(): string {
  if (typeof window === 'undefined') return 'http://localhost:4200';
  const { protocol, hostname } = window.location;
  if (platformHostSuffix(hostname)) {
    return `https://${hostname.replace(/^builder\./, 'admin.')}`;
  }
  if (hostname.endsWith('.aquilixshop.localhost')) {
    return 'http://admin.aquilixshop.localhost';
  }
  return `${protocol}//localhost:4200`;
}

/**
 * Where the topbar's back arrow lands: the dashboard **Themes** page, not the dashboard root (which
 * redirects to Products). The builder is only ever entered from Themes (sidebar "Theme builder" or
 * Themes → Customize), so leaving it must put the seller back where they started — founder feedback
 * 2026-07-31.
 */
export function dashboardExitUrl(): string {
  return `${deriveDashboardUrl()}/themes`;
}

/**
 * The dashboard login URL to bounce to on a failed silent refresh, carrying the current builder URL
 * as `returnUrl` so the dashboard can route the seller back after re-authenticating.
 */
export function dashboardLoginUrl(): string {
  const current = typeof window !== 'undefined' ? window.location.href : '';
  return `${deriveDashboardUrl()}/login?returnUrl=${encodeURIComponent(current)}`;
}

/**
 * The PUBLIC storefront URL for one of the seller's stores — the live shop a customer sees. The
 * "Preview" button in the topbar opens this in a NEW tab. Derived from the builder hostname exactly
 * like `deriveApiBaseUrl` / `deriveDashboardUrl`: the `builder.` host prefix is swapped for the store
 * `slug`. In plain-localhost dev the storefront has no per-store subdomain, so the dev `?store=<slug>`
 * shortcut on the Astro dev port (4321) selects the store (mirrors storefront `resolveForwardHost`).
 *  - `builder.<x>.aquilixshop.com` → `https://<slug>.<x>.aquilixshop.com`
 *  - `builder.aquilixshop.localhost` (Caddy dev) → `http://<slug>.aquilixshop.localhost`
 *  - plain localhost dev → `http://localhost:4321/?store=<slug>`
 *
 * `slug` is a backend-validated store slug (`[a-z0-9-]`, reserved-name-checked), so it is safe verbatim
 * as a subdomain label; it is still URL-encoded for the `?store=` query form.
 *
 * `primaryDomain` (feature 0046) is the store's verified custom domain marked primary, when it has one.
 * On a DEPLOYED builder it wins — that IS the shop's public address, the one the dashboard's "view store"
 * eye opens; rebuilding `<slug>.<env>.aquilixshop.com` from the slug alone sent the seller to the old
 * address every time (founder, 05/10/2026). On a laptop it is deliberately ignored, exactly like the
 * dashboard's `deriveStorefrontUrl`: the custom domain points at the real edge, and a "preview" that
 * leaves the local stack mid-development is a trap (the dev guard).
 */
export function deriveStorefrontUrl(slug: string, primaryDomain?: string | null): string {
  const query = encodeURIComponent(slug);
  if (typeof window === 'undefined') return `http://localhost:4321/?store=${query}`;
  const { protocol, hostname } = window.location;
  if (platformHostSuffix(hostname)) {
    return primaryDomain
      ? `https://${primaryDomain}`
      : `https://${hostname.replace(/^builder\./, `${slug}.`)}`;
  }
  if (hostname.endsWith('.aquilixshop.localhost')) {
    return `http://${slug}.aquilixshop.localhost`;
  }
  return `${protocol}//localhost:4321/?store=${query}`;
}

/**
 * The storefront URL that renders a seller's UNPUBLISHED drafts (feature 0034) — the same public storefront
 * as {@link deriveStorefrontUrl}, with the preview grant attached.
 *
 * The grant is opaque to the builder: it does not decode it, cannot widen it, and cannot make one itself.
 * The backend minted it from the seller's JWT, and re-verifies it (signature, audience, expiry, store) on
 * every read. All that happens here is string assembly.
 *
 * `_previewName` is the theme name for the storefront's banner. Cosmetic and separate from the grant on
 * purpose: it only ever renders on a page the backend already agreed to serve, and the storefront sanitises
 * it before it reaches the DOM (`previewLabelFrom`).
 *
 * `primaryDomain` goes through to {@link deriveStorefrontUrl}: the grant only CONFIRMS the store the host
 * resolves to (`StorefrontPreviewController`), and the custom domain resolves to the same store, so the
 * draft previews on the address the seller actually knows.
 */
export function derivePreviewUrl(
  slug: string,
  token: string,
  themeName?: string | null,
  primaryDomain?: string | null,
): string {
  const base = deriveStorefrontUrl(slug, primaryDomain);
  const sep = base.includes('?') ? '&' : '?';
  const name = themeName?.trim() ? `&_previewName=${encodeURIComponent(themeName.trim())}` : '';
  return `${base}${sep}_preview=${encodeURIComponent(token)}${name}`;
}
